<template>
  <div>
    <div class="step-panel content-workbench">
      <div class="section-head section-head--compact">
        <div>
          <span class="section-label">第二步</span>
          <h2>先生成初稿，再改成你想要的内容</h2>
          <p>系统会先看看素材参考中心里有没有合适的内容，也可以根据你的补充要求重新生成。只有你确认过的版本，才会加入中心素材。</p>
        </div>
      </div>

      <div class="content-toolbar">
        <div class="content-toolbar__group">
          <GenerationModelSplitButton
            class="toolbar-button-split"
            action-label="生成初稿"
            :options="contentSeedModelOptions"
            :model-value="contentSeedModelId"
            :loading="contentGeneratingAction === 'generate' && contentWaitingForFirstChunk"
            :disabled="isGeneratingContent && contentGeneratingAction !== 'generate'"
            @update:model-value="handleUpdateContentSeedModel"
            @trigger="emit('generate-content', { forceRefresh: false, textModelId: contentSeedModelId || undefined })"
          />
          <el-button
            class="toolbar-button"
            plain
            :loading="contentGeneratingAction === 'refresh' && contentWaitingForFirstChunk"
            :disabled="isGeneratingContent && contentGeneratingAction !== 'refresh'"
            @click="emit('generate-content', { forceRefresh: true, textModelId: contentSeedModelId || undefined })"
          >换一版</el-button>
          <el-button class="toolbar-button" plain :disabled="isGeneratingContent" @click="showCustomPromptModel = !showCustomPromptModel">
            {{ showCustomPromptModel ? '收起补充要求' : '补充生成要求' }}
          </el-button>
        </div>
        <div class="content-toolbar__group content-toolbar__group--end">
          <el-button class="toolbar-button" plain :disabled="!canConfirmContent || isGeneratingContent" :loading="contentConfirming" @click="emit('confirm-content')">
            确认加入素材参考中心
          </el-button>
        </div>
      </div>

      <div v-if="showCustomPromptModel" class="prompt-panel">
        <div class="section-head section-head--compact">
          <div>
            <span class="section-label">补充要求（选填）</span>
            <p>你可以补充人物关系、情绪、笑点、反转方式或禁区要求。不填也可以，系统会按内容类型自动生成。</p>
          </div>
        </div>
        <el-input
          v-model="customPromptModel"
          type="textarea"
          :rows="4"
          maxlength="2000"
          show-word-limit
          placeholder="例如：主角是刚入职的实习生，整体轻松一点，结尾有小反转，但不要太夸张。"
        />
      </div>

      <div class="status-grid">
        <article class="status-card">
          <span>当前来源</span>
          <strong>{{ getContentSourceLabel(contentMeta.source) }}</strong>
        </article>
        <article class="status-card">
          <span>同类素材数量</span>
          <strong>{{ contentMeta.libraryCount }}</strong>
        </article>
        <article class="status-card">
          <span>当前字数</span>
          <strong>{{ contentLength }}</strong>
        </article>
        <article class="status-card">
          <span>收录状态</span>
          <strong>{{ contentConfirmed ? '已加入素材库' : '尚未加入素材库' }}</strong>
        </article>
      </div>

      <el-form-item class="content-workbench__editor-item" label="内容初稿，可直接修改">
        <div class="content-editor-shell">
          <el-input
            v-model="formData.contentBody"
            class="content-editor-input"
            type="textarea"
            :rows="12"
            maxlength="10000"
            show-word-limit
            placeholder="先选择内容类型并点击“生成初稿”，结果会自动填到这里。你可以直接修改、删减、重写。"
            @input="emit('content-body-input')"
          />
        </div>
      </el-form-item>
    </div>

    <div class="step-panel product-panel">
      <div class="section-head section-head--compact">
        <div>
          <span class="section-label">第三步</span>
          <h2>选择是否需要做内容整合</h2>
          <p>如果这次项目只想先用内容本身推进，可以直接继续；如果要把产品卖点自然融进剧情，再走整合分支。</p>
        </div>
      </div>

      <div class="entry-choice-grid">
        <button
          type="button"
          class="entry-choice-card"
          :class="{ 'entry-choice-card--active': contentCreationModeModel === 'direct' }"
          @click="contentCreationModeModel = 'direct'"
        >
          <span>直接继续</span>
          <strong>使用当前内容创建项目</strong>
          <p>适合纯故事、段子或先把内容跑通的项目，不做额外产品植入。</p>
        </button>
        <button
          type="button"
          class="entry-choice-card"
          :class="{ 'entry-choice-card--active': contentCreationModeModel === 'embed' }"
          @click="contentCreationModeModel = 'embed'"
        >
          <span>软植入增强</span>
          <strong>先整合产品信息再创建</strong>
          <p>适合内容种草、剧情带货或需要把产品卖点自然融进内容的项目。</p>
        </button>
      </div>

      <template v-if="contentCreationModeModel === 'embed'">
        <div class="form-grid">
          <el-form-item label="产品名称">
            <el-input v-model="formData.productName" maxlength="120" placeholder="例如：轻养茶饮冻干杯" @input="emit('integration-inputs-changed')" />
          </el-form-item>
          <el-form-item label="产品使用场景（可选）">
            <el-input v-model="formData.productScene" maxlength="300" placeholder="例如：加班提神、通勤路上、办公室备着喝" @input="emit('integration-inputs-changed')" />
          </el-form-item>
        </div>

        <el-form-item label="产品卖点">
          <el-input
            v-model="formData.productFeatures"
            type="textarea"
            :rows="4"
            maxlength="800"
            show-word-limit
            placeholder="请写清楚产品最想被带出的特点，比如功能、优势、使用感受或和同类产品的区别。"
            @input="emit('integration-inputs-changed')"
          />
        </el-form-item>

        <div class="integration-panel">
          <div class="integration-panel__copy">
            <span class="section-label">第四步（可选执行）</span>
            <h3>启动内容整合</h3>
            <p>{{ integrationHint }}</p>
          </div>
          <GenerationModelSplitButton
            class="integration-button-split"
            :action-label="hasEmbeddedContent ? '重新启动内容整合' : '启动内容整合'"
            :options="contentEmbedModelOptions"
            :model-value="contentEmbedModelId"
            :disabled="!canStartIntegration"
            :loading="contentEmbedding && contentEmbeddingWaitingForFirstChunk"
            size="large"
            @update:model-value="handleUpdateContentEmbedModel"
            @trigger="emit('embed-product', { textModelId: contentEmbedModelId || undefined })"
          />
        </div>

        <div class="brief-preview brief-preview--content-driven">
          <div>
            <span class="section-label">整合后内容预览</span>
            <div class="preview-pills">
              <span class="preview-pill">{{ hasEmbeddedContent ? '产品信息已融入内容' : '还没融入产品信息' }}</span>
              <span v-if="sourceContent" class="preview-pill">已生成内容初稿</span>
              <span class="preview-pill">{{ contentConfirmed ? '已加入素材参考中心' : '还没加入素材参考中心' }}</span>
              <span v-if="formData.contentType" class="preview-pill">内容类型：{{ formData.contentType }}</span>
            </div>
          </div>
          <div class="integration-editor-shell">
            <el-input
              v-model="embeddedContentModel"
              class="content-editor-input content-editor-input--embedded"
              type="textarea"
              :rows="10"
              maxlength="10000"
              show-word-limit
              placeholder="点击“启动内容整合”后，这里会生成整合后的版本。这个文本框和上面的内容初稿独立，你可以继续单独修改。"
              @input="emit('embedded-content-input')"
            />
          </div>
        </div>
      </template>

      <div v-else class="brief-preview brief-preview--content-driven">
        <div>
          <span class="section-label">当前将直接进入项目的内容</span>
          <div class="preview-pills">
            <span class="preview-pill">不做内容整合</span>
            <span v-if="formData.contentBody.trim()" class="preview-pill">使用当前内容稿</span>
            <span class="preview-pill">{{ contentConfirmed ? '已加入素材参考中心' : '还没加入素材参考中心' }}</span>
            <span v-if="formData.contentType" class="preview-pill">内容类型：{{ formData.contentType }}</span>
          </div>
        </div>
        <p class="preview-copy">{{ formData.contentBody || '生成或填写的内容会直接作为项目正文继续进入后续方案、剧本和分镜流程。' }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import GenerationModelSplitButton from '@/components/GenerationModelSplitButton.vue'
import { useModelCatalogStore } from '@/stores/modelCatalog'
import { resolveGenerationModelSelection, setGenerationModelSelection } from '@/utils/generationModelSelection'

type ContentGeneratingAction = 'generate' | 'refresh' | null
type ContentCreationMode = 'direct' | 'embed'

interface ContentDrivenFormState {
  contentType: string
  contentBody: string
  productName: string
  productFeatures: string
  productScene: string
}

interface ContentMetaState {
  source: string
  libraryCount: number
}

const props = defineProps<{
  formData: ContentDrivenFormState
  contentGeneratingAction: ContentGeneratingAction
  contentWaitingForFirstChunk: boolean
  isGeneratingContent: boolean
  showCustomPrompt: boolean
  customPrompt: string
  canConfirmContent: boolean
  contentConfirming: boolean
  contentMeta: ContentMetaState
  contentLength: number
  contentConfirmed: boolean
  contentCreationMode: ContentCreationMode
  integrationHint: string
  canStartIntegration: boolean
  contentEmbedding: boolean
  contentEmbeddingWaitingForFirstChunk: boolean
  hasEmbeddedContent: boolean
  sourceContent: string
  embeddedContent: string
  getContentSourceLabel: (source: string) => string
}>()

const emit = defineEmits<{
  'generate-content': [payload: { forceRefresh: boolean; textModelId?: string }]
  'update:showCustomPrompt': [value: boolean]
  'update:customPrompt': [value: string]
  'confirm-content': []
  'content-body-input': []
  'update:contentCreationMode': [value: ContentCreationMode]
  'integration-inputs-changed': []
  'embed-product': [payload: { textModelId?: string }]
  'update:embeddedContent': [value: string]
  'embedded-content-input': []
}>()

const modelCatalogStore = useModelCatalogStore()
const contentSeedModelId = ref('')
const contentEmbedModelId = ref('')

const contentSeedModelOptions = computed(() => modelCatalogStore.textModelsForOperation('content_seed_generate'))
const contentEmbedModelOptions = computed(() => modelCatalogStore.textModelsForOperation('content_embed'))

function handleUpdateContentSeedModel(value: string) {
  contentSeedModelId.value = value
  setGenerationModelSelection('text', 'content_seed_generate', value)
}

function handleUpdateContentEmbedModel(value: string) {
  contentEmbedModelId.value = value
  setGenerationModelSelection('text', 'content_embed', value)
}

watch(contentSeedModelOptions, (options) => {
  if (!options.length) {
    contentSeedModelId.value = ''
    return
  }
  if (!contentSeedModelId.value || !options.some((item) => item.id === contentSeedModelId.value)) {
    contentSeedModelId.value = resolveGenerationModelSelection('text', 'content_seed_generate', options)
  }
}, { immediate: true })

watch(contentEmbedModelOptions, (options) => {
  if (!options.length) {
    contentEmbedModelId.value = ''
    return
  }
  if (!contentEmbedModelId.value || !options.some((item) => item.id === contentEmbedModelId.value)) {
    contentEmbedModelId.value = resolveGenerationModelSelection('text', 'content_embed', options)
  }
}, { immediate: true })

onMounted(() => {
  void modelCatalogStore.ensureLoaded()
})

const showCustomPromptModel = computed({
  get: () => props.showCustomPrompt,
  set: (value: boolean) => emit('update:showCustomPrompt', value),
})

const customPromptModel = computed({
  get: () => props.customPrompt,
  set: (value: string) => emit('update:customPrompt', value),
})

const contentCreationModeModel = computed({
  get: () => props.contentCreationMode,
  set: (value: ContentCreationMode) => emit('update:contentCreationMode', value),
})

const embeddedContentModel = computed({
  get: () => props.embeddedContent,
  set: (value: string) => emit('update:embeddedContent', value),
})
</script>

<style scoped>
.section-head {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: flex-start;
}

.section-head--compact h2 {
  font-size: 26px;
}

.section-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a5a1f;
}

.section-head h2,
.integration-panel h3 {
  margin: 0;
  color: #10243d;
  letter-spacing: -0.04em;
}

.section-head p,
.integration-panel p {
  margin: 12px 0 0;
  color: #526173;
  line-height: 1.75;
}

.step-panel {
  padding: 24px;
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.96));
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.05);
}

.content-workbench,
.product-panel {
  display: grid;
  gap: 18px;
  margin-top: 8px;
  margin-bottom: 22px;
}

.content-toolbar,
.integration-panel__copy {
  display: grid;
  gap: 10px;
}

.content-toolbar {
  gap: 14px;
}

.content-toolbar__group {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.content-toolbar__group--end {
  justify-content: flex-start;
}

.toolbar-button {
  min-height: 44px;
  padding-inline: 18px;
  border-radius: 999px;
}

.toolbar-button--primary {
  box-shadow: 0 14px 30px rgba(37, 99, 235, 0.18);
}

.prompt-panel {
  padding: 18px;
  border-radius: 22px;
  background: linear-gradient(135deg, #f0fdfa 0%, #f8fafc 100%);
  border: 1px solid rgba(13, 148, 136, 0.18);
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.status-card {
  padding: 18px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.05);
}

.status-card span {
  display: block;
  color: #64748b;
  font-size: 13px;
}

.status-card strong {
  display: block;
  margin-top: 10px;
  color: #10243d;
  font-size: 20px;
  line-height: 1.35;
}

.content-workbench__editor-item {
  margin-bottom: 0;
}

.content-workbench__editor-item :deep(.el-form-item__content) {
  width: 100%;
}

.content-workbench__editor-item :deep(.el-form-item__label) {
  width: 100%;
  justify-content: flex-start;
  padding-bottom: 10px;
}

.content-editor-shell {
  position: relative;
  width: 100%;
  min-height: 320px;
}

.integration-editor-shell {
  width: 100%;
}

.content-editor-shell :deep(.el-textarea),
.integration-editor-shell :deep(.el-textarea) {
  display: block;
  width: 100%;
}

.content-editor-shell :deep(.el-textarea__inner),
.integration-editor-shell :deep(.el-textarea__inner) {
  width: 100%;
  min-height: 320px;
  padding: 18px 20px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
  font-size: 15px;
  line-height: 1.8;
}

.integration-editor-shell :deep(.el-textarea__inner) {
  min-height: 360px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.entry-choice-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.entry-choice-card {
  padding: 20px;
  border-radius: 22px;
  border: 1px solid rgba(148, 163, 184, 0.24);
  background: rgba(248, 250, 252, 0.92);
  text-align: left;
  cursor: pointer;
  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.entry-choice-card span,
.entry-choice-card strong {
  display: block;
}

.entry-choice-card span {
  font-size: 13px;
  color: #8a5a1f;
}

.entry-choice-card strong {
  margin-top: 10px;
  font-size: 22px;
  color: #10243d;
}

.entry-choice-card p {
  margin: 12px 0 0;
  color: #526173;
  line-height: 1.7;
}

.entry-choice-card--active {
  border-color: rgba(13, 148, 136, 0.45);
  box-shadow: 0 16px 36px rgba(15, 118, 110, 0.14);
  transform: translateY(-2px);
  background: linear-gradient(135deg, rgba(240, 253, 250, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%);
}

.integration-panel {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 18px;
  padding: 22px;
  border-radius: 24px;
  background: linear-gradient(135deg, #0f766e 0%, #155e75 100%);
  color: #f8fafc;
}

.integration-panel .section-label,
.integration-panel h3,
.integration-panel p {
  color: inherit;
}

.integration-button {
  min-width: 220px;
  min-height: 52px;
  border-radius: 999px;
  box-shadow: 0 18px 34px rgba(8, 47, 73, 0.28);
}

.brief-preview {
  padding: 20px;
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: linear-gradient(135deg, #fffbeb 0%, #f0fdfa 100%);
}

.brief-preview--content-driven {
  background: linear-gradient(135deg, #ecfeff 0%, #f8fafc 100%);
}

.preview-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.preview-pill {
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  color: #0f766e;
  font-size: 13px;
}

.preview-copy {
  margin: 14px 0 0;
  color: #475569;
  line-height: 1.75;
  white-space: pre-wrap;
}

@media (max-width: 1100px) {
  .status-grid,
  .integration-panel,
  .entry-choice-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .form-grid,
  .content-toolbar__group,
  .integration-panel {
    display: grid;
    grid-template-columns: 1fr;
  }

  .status-grid {
    grid-template-columns: 1fr;
  }
}
</style>